import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import _isEmpty from 'lodash/isEmpty';
import { isEqualCuePoint } from '@bedrock-rdk/media-engine/src/engines/shaka/textDisplayer/textDisplayer.utils';
import {
  CUE_ACTION,
  TEXT_RENDERER_V4,
} from '@bedrock-rdk/media-engine/src/engines/shaka/textDisplayer/textDisplayer.constants';
import { defaultContext, getDefaultRegionStyle, getInitialStyle, getItemStyle } from './subtitles.style';
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_REGION, writingMode } from './subtitles.constants';
import { convertLengthValue, getDimensionsByRegion, getDimensionsBySize, parseColor } from './subtitles.utils';
import connect from './subtitles.connect';

export class Subtitles extends Lightning.Component {
  static _template() {
    return getInitialStyle();
  }

  _construct() {
    this._screenSize = defaultContext.screenSize;
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  updateSubtitles(payload) {
    if (!payload) {
      return;
    }

    const { cuePointState: action, ...parent } = payload;

    if (action === CUE_ACTION.REMOVED) {
      if (_isEmpty(parent)) {
        this._clearAllRegions();
      } else {
        this._removeItem(parent);
      }

      return;
    }

    // parse container cue
    this._parseCue(parent);

    // parse nested cues
    if (parent.version === TEXT_RENDERER_V4) {
      const parentBackgroundColor = parseColor(parent.backgroundColor);
      (parent.nestedCues || [])
        .filter(cue => Boolean(cue.payload))
        .forEach(child => {
          this._parseCue({
            ...child,
            backgroundColor: parseColor(child.backgroundColor) || parentBackgroundColor || DEFAULT_BACKGROUND_COLOR,
            region: parent.region,
          });
        });
    } else {
      parent.nestedCues.forEach(nestedCue => {
        this._parseCue(nestedCue, true);
      });
    }
  }

  // eslint-disable-next-line no-unused-vars
  _parseCue(cue) {
    // skip line break;
    if (this._isLineBreak(cue) || !(cue.payload && cue.payload.length)) {
      return;
    }

    // display container cue
    const regionId = this._getRegionId(cue);

    // container dimensions support by region data
    if (cue.region && cue.region.id) {
      this._applyRegionStyle(regionId, getDimensionsByRegion(cue, this._screenSize));
    }

    // container dimensions support by size data
    if (cue.size) {
      this._applyRegionStyle(regionId, getDimensionsBySize(cue.size, cue.writingMode, this._screenSize));
    }

    this._addItem(cue);
  }

  _getRegionById(regionId) {
    return this.tag(`Regions.${regionId}`);
  }

  _applyRegionStyle(regionId, style) {
    if (!this._getRegionById(regionId)) {
      this.tag('Regions').patch({
        [regionId]: getDefaultRegionStyle(this._screenSize),
      });
    }

    this.tag('Regions').patch({
      [regionId]: style,
    });
  }

  _addItem(cue) {
    const regionId = this._getRegionId(cue);
    const region = this._getRegionById(regionId);
    const currentCues = region.children;
    const styledItem = _merge(getItemStyle(), this._parseCueStyle(cue, region));
    region.children = [...currentCues, styledItem];
  }

  _removeItem(cue, regionId) {
    const parentRegionId = regionId || this._getRegionId(cue);
    const region = this._getRegionById(parentRegionId);

    if (cue.nestedCues) {
      cue.nestedCues.forEach(nestedCue => this._removeItem(nestedCue, parentRegionId));
    }

    if (region) {
      region.children = region.children.filter(child => !isEqualCuePoint(child.metadata, cue));
    }
  }

  _clearAllRegions() {
    this.tag('Regions').children = [];
  }

  _isLineBreak(cue) {
    return Boolean(cue.lineBreak || cue.spacer);
  }

  _getRegionId(cue) {
    if (cue.region && cue.region.id) {
      return `Region_${cue.region.id}`;
    }

    return `Region_${DEFAULT_REGION}`;
  }

  _parseCueStyle(cue, region) {
    const text = {
      text: cue.payload,
      wordWrapWidth: region.finalW,
    };

    const result = {
      text,
      metadata: cue,
      flexItem: {
        alignSelf: 'center',
        // NOTE: currently it's always "start", ignoring this property
        // alignSelf: textAlignToFlex(cue.textAlign),
      },
    };

    if (cue.backgroundColor) {
      text.highlight = true;
      text.highlightColor = Number.isFinite(cue.backgroundColor)
        ? cue.backgroundColor
        : parseColor(cue.backgroundColor);
    }

    if (cue.color) {
      text.textColor = parseColor(cue.color);
    }

    if (cue.fontFamily) {
      text.fontFace = cue.fontFamily;
    }

    if (cue.fontSize) {
      // TODO: clarify how this value will look like to check if we parse it properly
      text.fontSize = convertLengthValue(text.fontSize, cue, this._screenSize);
    }

    if (cue.fontStyle) {
      text.fontStyle = cue.fontStyle;
    }

    if (cue.lineHeight) {
      text.lineHeight = cue.lineHeight;
    }

    // The position defines the indent of the text in the direction defined by the writing direction
    if (cue.position) {
      if (cue.writingMode === writingMode.HORIZONTAL_TOP_TO_BOTTOM) {
        text.paddingLeft = cue.position;
      }
    }

    return result;
  }
}
